import { Fragment } from "react";

import clsx from "clsx";

import { GenericTooltip } from "@components/tooltip-container";
import { CompareCustomDateRangeSelect } from "@features/sales-v3/top-level-filters/components/compare-date-range-menu/compare-custom-date-range-select";
import { SamePeriodDifferentYearSubmenu } from "@features/sales-v3/top-level-filters/components/compare-date-range-menu/same-period-different-year-submenu";
import { deriveComparisonDateRange } from "@features/sales-v3/utils/compare-dates-helpers";
import { formatDateRange } from "@features/sales-v3/utils/format-date-range";
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import {
  constants,
  type CompareDateRange,
  type DateInterval,
  type ITimeGranularityV2,
} from "@north-beam/nb-common";
import { renderChevronAnimation } from "@shared/selects/utils";

interface Props {
  isDisabled?: boolean;
  timeGranularity: ITimeGranularityV2;
  compareDateRange: CompareDateRange | null;
  dateRangeInterval: DateInterval;
  timezone: string;
  onCompareDateRangeChange: (value: CompareDateRange | null) => void;
}

export const CompareDateSelect = ({
  isDisabled,
  timeGranularity,
  dateRangeInterval,
  compareDateRange,
  timezone,
  onCompareDateRangeChange,
}: Props) => {
  const compareDateRangeInterval = compareDateRange
    ? deriveComparisonDateRange(
        dateRangeInterval,
        compareDateRange,
        timezone,
        timeGranularity,
      )
    : null;

  const compareDateRangeDisplayText = compareDateRangeInterval
    ? formatDateRange(compareDateRangeInterval)
    : "";

  return (
    <div>
      <label
        className={clsx("font-bold text-[11px] mb-1.5 label", {
          "text-disabled": isDisabled,
        })}
      >
        TIME COMPARISON
        <GenericTooltip
          content={
            <div className="flex flex-col w-64 space-y-2">
              <div className="font-bold tracking-wide">Time Comparison</div>
              <div>
                Time period specific to this metric compared to the main date
                range.
              </div>
            </div>
          }
        />
      </label>
      <Popover
        className={clsx("relative text-sm", { "opacity-60": isDisabled })}
      >
        <PopoverButton
          disabled={isDisabled}
          as="div"
          className={clsx("nb-input-container")}
        >
          {({ open }) => (
            <div
              className={clsx(
                "nb-input flex justify-between items-center bg-v2-primary cursor-pointer h-inherit",
                {
                  "border border-cosmic ring-1 ring-cosmic": open,
                  "border-gray": isDisabled,
                },
              )}
            >
              <div
                className={clsx(
                  "text-ellipsis whitespace-nowrap overflow-hidden",
                  "max-w-full flex-grow text-left",
                )}
              >
                {!compareDateRange && (
                  <span className="text-disabled">Compare to...</span>
                )}
                {compareDateRange &&
                  constants.CompareDateRangeTypes.getLabel(
                    compareDateRange.value,
                  )}
              </div>
              <span
                className="pl-2 justify-self-end flex items-center gap-2.5"
                onClick={(event) => {
                  if (!open) {
                    event.preventDefault();
                    event.stopPropagation();
                  }

                  onCompareDateRangeChange(null);
                }}
              >
                {compareDateRange && (
                  <i
                    className={clsx(
                      "fa-regular fa-sharp fa-xmark text-sm hover:text-cosmic",
                    )}
                  />
                )}
                {renderChevronAnimation(open)}
              </span>
            </div>
          )}
        </PopoverButton>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-200"
          enterFrom="opacity-0 translate-y-1"
          enterTo="opacity-100 translate-y-0"
          leave="transition ease-in duration-150"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 translate-y-1"
        >
          <PopoverPanel
            anchor={{ to: "bottom", gap: 4 }}
            className={clsx(
              "rounded-md bg-v2-primary elevation-1 py-1 z-[2003] text-sm w-[var(--button-width)]",
            )}
          >
            {({ close }) => (
              <>
                <div
                  className={clsx(
                    "hover:cursor-pointer hover:bg-cosmic-5 hover:font-semibold px-4 py-1.5",
                    "flex items-center justify-between",
                  )}
                  onClick={() => {
                    onCompareDateRangeChange({
                      value: "previousPeriod",
                    });
                    close();
                  }}
                >
                  <span className="text-interstellar leading-loose">
                    Previous period
                  </span>
                </div>
                <div
                  className={clsx(
                    "hover:cursor-pointer hover:bg-cosmic-5 hover:font-semibold px-4 py-1.5",
                    "flex items-center justify-between",
                  )}
                  onClick={() => {
                    onCompareDateRangeChange({
                      value:
                        constants.CompareDateRangeTypes.SAMEPERIODPRIORYEAR,
                    });
                    close();
                  }}
                >
                  <span className="text-interstellar leading-loose">
                    Same period, prior year
                  </span>
                </div>
                <SamePeriodDifferentYearSubmenu
                  selectSamePeriodDifferentYear={(
                    calendar: "standard",
                    year: number,
                  ) => {
                    onCompareDateRangeChange({
                      value: "samePeriodDifferentYear",
                      year,
                      calendar,
                    });
                    close();
                  }}
                  compareDateRange={compareDateRange}
                />
                <CompareCustomDateRangeSelect
                  timeGranularity={timeGranularity}
                  compareDateRange={compareDateRange}
                  placement="right"
                  selectCustomDateRange={({ startDate, endDate }) => {
                    onCompareDateRangeChange({
                      value: "custom",
                      startDate,
                      endDate,
                    });
                    close();
                  }}
                />
              </>
            )}
          </PopoverPanel>
        </Transition>
      </Popover>
      <div className="text-xs text-gray+1 mt-0.5 ml-0.5">
        {compareDateRangeDisplayText}
      </div>
    </div>
  );
};
